import React, { useCallback, useRef, useState } from 'react';
import Cropper from 'react-cropper';
import { useTranslation } from 'react-i18next';
import { Modal } from '@carbon/react';
import { useUpload } from 'src/hooks/useUpload';
import { postMediaUploadUrl } from 'src/services/apis/services';
import { encodeCanvasToBlurhash } from 'src/utils/blurhash';
import { canvasToWebp, scaleCanvas, MAX_ORIGINAL_PX, THUMB_PX } from 'src/utils/canvas';

export function CropperModal(props: {
  croppingFile: { file: File | null; previewUrl: string } | null;
  onClose: () => void;
  cropperAspectRatio: number;
  onChange: ({ fileId, blurhash, previewUrl }) => void;
}) {
  const { t } = useTranslation();
  const { onChange } = props;
  const upload = useUpload();
  const cropperRef = useRef<any>(null);
  const [cropper, setCropper] = useState<Cropper>();
  const [cropping, setCropping] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleCropImageAndUpload = useCallback(async () => {
    if (typeof cropper === 'undefined') return;

    setCropping(true);
    try {
      const canvas = cropper.getCroppedCanvas();
      const original = await canvasToWebp(scaleCanvas(canvas, MAX_ORIGINAL_PX));
      const thumbCanvas = scaleCanvas(canvas, THUMB_PX);
      const thumb = await canvasToWebp(thumbCanvas);

      setUploading(true);
      const { fileId, uploadUrl, thumbUploadUrl } = await postMediaUploadUrl({
        contentType: 'image/webp',
        contentLength: original.size,
        thumbContentLength: thumb.size,
      });
      // thumbContentLength was sent above, so the server contract guarantees a thumb URL back;
      // the response type is optional only because other callers (e.g. PDFs) omit the request field.
      if (!thumbUploadUrl) throw new Error('Expected a thumb upload URL: thumbContentLength was sent');

      // Both objects or neither: a thumb-less key renders a broken avatar, and an original-less
      // key renders nothing at all.
      await Promise.all([upload(uploadUrl, original), upload(thumbUploadUrl, thumb)]);

      // Encoded from the thumb canvas: a blurhash is 4x4 components, so the extra pixels in the
      // original buy nothing and cost a full-size getImageData.
      const blurhash = encodeCanvasToBlurhash(thumbCanvas);

      onChange({ fileId, blurhash, previewUrl: URL.createObjectURL(original) });
    } finally {
      // Always runs, including on a rejected presign or PUT, so a misconfigured bucket (the
      // expected first experience on a fresh environment) leaves the button clickable again
      // instead of stuck on "Uploading..." with no way to retry but a reload.
      setCropping(false);
      setUploading(false);
    }
  }, [cropper, upload, onChange]);

  return (
    <Modal
      open={!!props.croppingFile}
      modalHeading={t('common.cropYourImage')}
      modalLabel={t('common.optimizeImage')}
      size={'sm'}
      primaryButtonText={
        uploading ? `${t('common.uploading')}...` : cropping ? `${t('common.cropping')}...` : t('actions.crop')
      }
      secondaryButtonText={t('actions.cancel')}
      primaryButtonDisabled={cropping}
      onRequestClose={props.onClose}
      onSecondarySubmit={props.onClose}
      onRequestSubmit={handleCropImageAndUpload}
    >
      <Cropper
        src={props.croppingFile?.previewUrl}
        style={{ height: 400, width: '100%' }}
        initialAspectRatio={props.cropperAspectRatio}
        guides={true}
        ref={cropperRef}
        checkOrientation={true}
        dragMode={'move'}
        onInitialized={(instance) => {
          setCropper(instance);
        }}
      />
    </Modal>
  );
}
